import {
  formatDurationSummary,
  type IngestEstimate,
  summarizeDuration,
} from '@lib/utils/ingestEstimate';
import { URLStatus } from '@stores/UploadURL/UploadURLStore';
import { formatCreditsWithUnit, formatDurationToString, pluralize } from './units';

type Status = 'error' | 'valid' | 'warning' | 'loading' | 'duplicate';

interface CreditsInfo {
  current: number;
  cost: number;
  left: number;
}

interface GetCreditStatusMessageParams {
  credits: CreditsInfo;
  videoCount?: {
    found: number;
    inLibrary: number;
    new: number;
  };
}

interface CreditStatusMessageResult {
  message: string;
  status: Status;
  missingCredits: number;
}

export function getCreditStatusMessage({
  credits,
  videoCount = { found: 1, inLibrary: 0, new: 1 },
}: GetCreditStatusMessageParams): CreditStatusMessageResult {
  const hasEnoughCredits = credits.current >= credits.cost;
  const status: Status = hasEnoughCredits ? 'valid' : 'error';
  const isMultipleVideos = videoCount.found > 1;
  const hasVideosInLibrary = videoCount.inLibrary > 0;
  const xCreditsCurrent = formatCreditsWithUnit(credits.current, 'down'); // credits left - round down
  const xCreditsCost = formatCreditsWithUnit(credits.cost, 'up'); // cost - round up
  const xCreditsLeft = formatCreditsWithUnit(credits.current - credits.cost, 'down'); // credits left after - round down

  let message: string = `You have enough credits to add all ${videoCount.found} media in this URL.`;

  if (!hasEnoughCredits) {
    // Case where there are not enough credits.
    if (isMultipleVideos) {
      message = `Not enough credits to add all media. Please select which media to add. • Adding all would consume ≈${xCreditsCost} • ≈${xCreditsCurrent} left now`;
    } else {
      message = `Not enough credits. • Processing will consume ≈${xCreditsCost} • ≈${xCreditsCurrent} left now`;
    }
  } else {
    // Case where there are enough credits.
    message = `Enough credits. • Processing will consume ≈${xCreditsCost} • ≈${xCreditsCurrent} left now • ≈${xCreditsLeft} left after`;
  }

  return { message, status, missingCredits: credits.cost - credits.current };
}

interface StorageInfo {
  current: number;
  cost: number;
  left: number;
}

interface GetStorageStatusMessageParams {
  storage: StorageInfo;
  videoCount?: {
    found: number;
    inLibrary: number;
    new: number;
  };
}

interface StorageStatusMessageResult {
  message: string;
  status: Status;
  missingStorage: number;
}

export function getStorageStatusMessage({
  storage,
  videoCount = { found: 1, inLibrary: 0, new: 1 },
}: GetStorageStatusMessageParams): StorageStatusMessageResult {
  const hasEnoughStorage = storage.current >= storage.cost;
  const status: Status = hasEnoughStorage ? 'valid' : 'error';
  let message: string = `You have enough storage to store the media.`;

  if (!hasEnoughStorage) {
    if (videoCount.found > 1) {
      message = `Not enough storage to add all media. Please select which media to add. • Adding all would take up ${formatDurationToString(storage.cost, { precision: 0, detail: 2 })} of storage • ${formatDurationToString(storage.current, { precision: 0, detail: 2 })} of storage left now`;
    } else {
      message = `Not enough storage. • Adding will take up ${formatDurationToString(storage.cost, { precision: 0, detail: 2 })} of storage • ${formatDurationToString(storage.current, { precision: 0, detail: 2 })} of storage left now`;
    }
  } else {
    message = `Enough storage. • Adding will take up ${formatDurationToString(storage.cost, { precision: 0, detail: 2 })} • ${formatDurationToString(storage.current, { precision: 0, detail: 2 })} of storage left now • ${formatDurationToString(storage.left, { precision: 0, detail: 2 })} of storage left after`;
  }

  return { message, status, missingStorage: storage.cost - storage.current };
}

export function getAddAllButtonTooltipMessage({
  creditStatusMessage,
  storageStatusMessage,
}: {
  creditStatusMessage: CreditStatusMessageResult;
  storageStatusMessage: StorageStatusMessageResult;
}): {
  value: boolean;
  reason: string;
} {
  const insufficientCredits = creditStatusMessage.missingCredits > 0;
  const insufficientStorage = storageStatusMessage.missingStorage > 0;

  if (insufficientCredits && insufficientStorage) {
    return {
      value: false,
      reason: `You need to add ${formatCreditsWithUnit(creditStatusMessage.missingCredits, 'up', 'more')} to add all media in this URL. Please top up your credits.`, // missing credits needed - round up
    };
  }
  if (insufficientCredits) {
    return {
      value: false,
      reason: `You need to add ${formatCreditsWithUnit(creditStatusMessage.missingCredits, 'up', 'more')} to add all media in this URL. Please top up your credits.`, // missing credits needed - round up
    };
  }
  if (insufficientStorage) {
    return {
      value: false,
      reason: `You need ${formatDurationToString(storageStatusMessage.missingStorage, { precision: 0, detail: 2 })} more of storage space to add all media in this URL. Please upgrade your plan or delete some media.`,
    };
  }
  return {
    value: true,
    reason: 'Click to add all media in this URL.',
  };
}

interface GetURLStatusMessageParams {
  urlStatus: URLStatus | null | undefined;
  errorMessage?: string | null;
  videoCount?: number;
  newVideoCount?: number;
  nonDuplicateEstimate?: IngestEstimate;
  urlType?: 'video' | 'playlist' | 'channel' | 'multiple';
  isUrlAmbiguous?: boolean;
  /** When loading, number of entities/videos found so far (SSE progress). */
  loadingProgressCount?: number;
  /** Title of the resolved media (single-video URLs only). Used in place of "Media found". */
  mediaTitle?: string;
  /** Channel/uploader name (single-video URLs only). Appended after duration. */
  mediaChannel?: string;
}

const MEDIA_TITLE_MAX_LENGTH = 40;

function truncateMediaTitle(title: string): string {
  // Replace " • " inside the title so it can't break the " • "-delimited
  // segments used by PreUploadStatusMessage to bold the first segment.
  const sanitized = title.trim().replace(/\s•\s/g, ' · ');
  if (sanitized.length <= MEDIA_TITLE_MAX_LENGTH) return sanitized;
  return `${sanitized.slice(0, MEDIA_TITLE_MAX_LENGTH - 1).trimEnd()}…`;
}

interface URLStatusMessageResult {
  message: string | null;
  status: Status | null;
}

const statusConfig: Record<URLStatus, { message: string; status: Status }> = {
  loading: {
    message: 'Checking link...',
    status: 'loading',
  },
  error: {
    message: 'An unexpected error occurred. Please contact support.',
    status: 'error',
  },
  duplicate: {
    message: 'This media is already in your library.',
    status: 'duplicate',
  },
  valid: {
    message: 'Ready for upload.',
    status: 'valid',
  },
};

export function getURLStatusMessage({
  urlStatus,
  errorMessage,
  videoCount,
  newVideoCount,
  nonDuplicateEstimate,
  urlType = 'video',
  isUrlAmbiguous = false,
  loadingProgressCount,
  mediaTitle,
  mediaChannel,
}: GetURLStatusMessageParams): URLStatusMessageResult | null {
  if (!urlStatus) return null;

  const config = statusConfig[urlStatus];
  if (!config) return null;

  // Handle error message dynamically
  let message = urlStatus === 'error' ? errorMessage || config.message : config.message;

  // Phase 3: show progressive loading count when SSE is streaming
  if (urlStatus === 'loading' && loadingProgressCount !== undefined && loadingProgressCount > 0) {
    message = `Loading... ${loadingProgressCount} media found`;
  }

  // Enhanced message for valid status only
  // Other statuses (loading, duplicate, error) use their default messages
  if (
    urlStatus === 'valid' &&
    videoCount !== undefined &&
    newVideoCount !== undefined &&
    nonDuplicateEstimate !== undefined &&
    videoCount > 0 &&
    newVideoCount > 0
  ) {
    const trimmedTitle = mediaTitle?.trim();
    const showTitleAsLabel =
      urlType === 'video' && !isUrlAmbiguous && trimmedTitle && trimmedTitle.length > 0;
    const typeText =
      urlType === 'multiple'
        ? 'Multiple URLs'
        : urlType === 'channel'
          ? 'Channel found'
          : urlType === 'playlist' || isUrlAmbiguous
            ? 'Playlist found'
            : showTitleAsLabel
              ? truncateMediaTitle(trimmedTitle)
              : 'Media found';
    const parts: string[] = [typeText];
    if (nonDuplicateEstimate.imageCount > 0) {
      parts.push(pluralize(nonDuplicateEstimate.imageCount, 'image'));
    }
    // Only a length worth stating: a bare "Unknown" or "0 sec" would read as a fact about the title.
    const duration = summarizeDuration(nonDuplicateEstimate);
    if (duration && nonDuplicateEstimate.durationSeconds > 0) {
      parts.push(formatDurationSummary(duration));
    }

    const trimmedChannel = mediaChannel?.trim().replace(/\s•\s/g, ' · ');
    if (urlType === 'video' && !isUrlAmbiguous && trimmedChannel) {
      parts.push(trimmedChannel);
    }

    message = parts.join(' • ');
  }

  return { message, status: config.status };
}
